/**
 * Utilities for building and parsing structured token arrays.
 *
 * When a block or helper expression is resolved, each space-separated token
 * in the original variable is checked against the schema. Tokens that resolve
 * to a variable description are marked as `isResolved: true`; operators,
 * helper names, and unknown tokens are `isResolved: false`.
 *
 * The token array is serialized as a JSON string and stored in node attributes
 * (e.g., `variableDescription` for blocks, `description` for inline tags).
 * Views parse it back to render resolved tokens with visual styling.
 */

import type { BlockContextItem } from '../types';
import type { SchemaResolver } from './schemaResolver';

export interface ResolvedToken {
  /** Display text (resolved description or original token) */
  text: string;
  /** Whether this token was resolved to a variable description */
  isResolved: boolean;
}

/**
 * Build a JSON token string from an expression by resolving each
 * space-separated token as a variable path.
 *
 * Returns null if no tokens were resolved (nothing changed).
 */
export function buildTokenString(
  variable: string,
  blockContext: BlockContextItem[],
  resolver: SchemaResolver,
): string | null {
  if (!variable) return null;

  const tokens = variable.split(/\s+/);
  let anyResolved = false;

  const result: ResolvedToken[] = tokens.map((token) => {
    const validation = resolver.validatePath(token, blockContext);
    if (validation.definition?.description && validation.definition.description !== token) {
      anyResolved = true;
      return { text: validation.definition.description, isResolved: true };
    }
    return { text: token, isResolved: false };
  });

  if (!anyResolved) return null;

  return JSON.stringify(result);
}

/**
 * Parse a token string back to an array of ResolvedToken objects.
 *
 * Returns null if the value is not a JSON token array (i.e., it's a plain
 * string description or null/empty).
 */
export function parseResolvedTokens(value: string | null): ResolvedToken[] | null {
  if (!value || !value.startsWith('[{')) return null;

  try {
    const tokens = JSON.parse(value) as unknown;
    if (
      Array.isArray(tokens) &&
      tokens.length > 0 &&
      tokens.every(
        (t: unknown) =>
          typeof t === 'object' &&
          t !== null &&
          typeof (t as ResolvedToken).text === 'string' &&
          typeof (t as ResolvedToken).isResolved === 'boolean'
      )
    ) {
      return tokens as ResolvedToken[];
    }
  } catch {
    // Not valid JSON — treat as plain string
  }

  return null;
}

/**
 * Get the plain-text display string from a token string or plain description.
 * Useful for tooltips and other contexts where token styling isn't needed.
 */
export function tokenStringToPlainText(value: string | null): string | null {
  if (!value) return null;

  const tokens = parseResolvedTokens(value);
  if (tokens) {
    return tokens.map((t) => t.text).join(' ');
  }

  return value;
}
